import { ParsedFilter } from '@ui-components/Searchbar/GenericSearchbar';

/**
 * Generic filter function that can be used with any data array and search filters
 * @param data - Array of objects to filter
 * @param filters - Array of ParsedFilter objects from GenericSearchbar
 * @param fieldMapping - Optional mapping of search keys to actual field names
 * @returns Filtered array of data
 */
/**
 * Normalizes a search key by removing spaces, underscores, hyphens and converting to lowercase
 * This allows flexible matching of field names regardless of formatting
 */
const normalizeSearchKey = (key: string): string => {
  return key.toLowerCase().replace(/[\s_-]/g, '');
};

/**
 * Finds the best matching field name from the mapping using flexible key matching
 */
const findMatchingField = (searchKey: string, fieldMapping?: { [key: string]: string }): string => {
  if (!fieldMapping) return searchKey;
  
  const normalizedSearchKey = normalizeSearchKey(searchKey);
  
  // First try exact match with the original key
  if (fieldMapping[searchKey.toLowerCase()]) {
    return fieldMapping[searchKey.toLowerCase()];
  }
  
  // Then try to find a match using normalized keys
  for (const [mappingKey, fieldName] of Object.entries(fieldMapping)) {
    const normalizedMappingKey = normalizeSearchKey(mappingKey);
    if (normalizedMappingKey === normalizedSearchKey) {
      return fieldName;
    }
  }
  
  // If no mapping found, return the original key
  return searchKey;
};

export const filterDataBySearch = <T>(
  data: T[],
  filters: ParsedFilter[],
  fieldMapping?: { [key: string]: string }
): T[] => {
  if (!filters || filters.length === 0) return data;
  
  return data.filter(item => {
    return filters.every(filter => {
      const { key, value } = filter;
      
      // If no key is specified (free text search), search across all fields
      if (!key || key.trim() === '') {
        const searchValue = String(value).toLowerCase();
        
        // Search across all relevant fields
        const searchableFields = Object.values(item as Record<string, unknown>);
        return searchableFields.some(fieldValue => {
          return String(fieldValue || '').toLowerCase().includes(searchValue);
        });
      }
      
      // Find the best matching field name using flexible matching
      const fieldName = findMatchingField(key.trim(), fieldMapping);
      
      // Get the field value from the item
      const fieldValue = (item as Record<string, unknown>)[fieldName];
      
      // Convert to string for comparison (case-insensitive)
      const fieldValueStr = String(fieldValue || '').toLowerCase();
      const searchValue = String(value).toLowerCase();
      
      // Simple contains search (case-insensitive)
      return fieldValueStr.includes(searchValue);
    });
  });
};

/**
 * Predefined field mappings for common data types
 */
export const FIELD_MAPPINGS = {
  // Cloud Elements mapping
  CLOUD_ELEMENTS: {
    'type': 'elementType',
    'element id': 'elementId',
    'vpc': 'vpcId',
    'vpc id': 'vpcId',
    'region': 'region',
    'tags': 'tags',
    'discovered at': 'discoveredAt'
  },
  
  // Interfaces mapping
  INTERFACES: {
    'interface': 'interface',
    'ip address': 'ipAddress',
    'mac address': 'macAddress',
    'vpc id': 'vpcId',
    'cloud subnet id': 'cloudSubnetId'
  },
  
  // Reflectors mapping
  REFLECTORS: {
    'id': 'id',
    'reflector id': 'name', // The reflector ID column actually maps to the 'name' field
    'name': 'description', // The name column actually maps to the 'description' field
    'description': 'description',
    'uuid': 'subId',
    'sub id': 'subId',
    'status': 'status',
    'cloud': 'cloud',
    'region': 'region',
    'cluster': 'cluster',
    'version': 'version',
    'last sync': 'lastSync',
    'created at': 'createdAt',
    'vpc id': 'vpcId',
    'tags': 'tags'
  },
  
  // BGP Neighbors mapping
  BGP_NEIGHBORS: {
    'neighbor ip': 'neighborAddress',
    'peer as': 'peerAs',
    'local as': 'localAs',
    'description': 'description',
    'status': 'status'
  },
  
  // Clusters mapping
  CLUSTERS: {
    'cluster id': 'id',
    'name': 'name',
    'status': 'status',
    'cloud': 'cloud',
    'region': 'region',
    'description': 'description',
    'reflectors': 'reflectors',
    'last sync': 'lastSync'
  }
} as const;

/**
 * Convenience function for filtering cloud elements
 */
export const filterCloudElements = <T>(
  data: T[],
  filters: ParsedFilter[]
): T[] => {
  return filterDataBySearch(data, filters, FIELD_MAPPINGS.CLOUD_ELEMENTS);
};

/**
 * Convenience function for filtering interfaces
 */
export const filterInterfaces = <T>(
  data: T[],
  filters: ParsedFilter[]
): T[] => {
  return filterDataBySearch(data, filters, FIELD_MAPPINGS.INTERFACES);
};

/**
 * Convenience function for filtering reflectors
 */
export const filterReflectors = <T>(
  data: T[],
  filters: ParsedFilter[]
): T[] => {
  return filterDataBySearch(data, filters, FIELD_MAPPINGS.REFLECTORS);
};

/**
 * Convenience function for filtering BGP neighbors
 */
export const filterBgpNeighbors = <T>(
  data: T[],
  filters: ParsedFilter[]
): T[] => {
  return filterDataBySearch(data, filters, FIELD_MAPPINGS.BGP_NEIGHBORS);
};

/**
 * Convenience function for filtering clusters
 */
export const filterClusters = <T>(
  data: T[],
  filters: ParsedFilter[]
): T[] => {
  return filterDataBySearch(data, filters, FIELD_MAPPINGS.CLUSTERS);
};
